import { DropdownWrapper, SpDropdownItem } from 'components/shared/Dropdown';
import { FormattedMessage } from 'components/shared/Intl';
import { useReadFromClipboard } from 'hooks/useReadFromClipboard';
import { ProductVariationBase } from 'models/productsReferential';
import { FC, useCallback, useMemo } from 'react';
import { DropdownMenu, DropdownToggle } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { importExcelToOrdersBasketWorkspaceThunk } from 'state/orders-basket/import-spreadsheet/thunks';
import { ordersBasketFormsImportThunk } from 'state/orders-basket/thunks';
import { ThunkDispatch } from 'state/state.common';
import { getToastUtils } from 'utils/toast';

interface OrdersBasketImportButtonProps {
  productSubType: string;
  productType: string;
}

export const OrdersBasketImportButton: FC<OrdersBasketImportButtonProps> = ({ productSubType, productType }) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const clipboardReader = useReadFromClipboard();
  const toast = useMemo(() => getToastUtils(dispatch), [dispatch]);

  const handleImportFromClipboard = useCallback(async () => {
    const identifiers = await clipboardReader.readCellsAsFlatArray(true);
    dispatch(
      ordersBasketFormsImportThunk(identifiers, {
        productSubType,
        productType,
      } as ProductVariationBase),
    );
  }, [clipboardReader, dispatch, productSubType, productType]);

  const handleImportFromFile = useCallback(async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.csv,text/csv';

    input.onchange = async (event: any) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      try {
        const data = await file.arrayBuffer();
        dispatch(importExcelToOrdersBasketWorkspaceThunk(data));
      } catch {
        toast.error({ id: 'quote.import.ordersBasket.failed' }, { id: 'quote.import.button' });
      }
    };
    input.click();
  }, [toast, dispatch]);

  const isSumitomo = productSubType.toLowerCase() === 'sumitomo';

  return (
    <DropdownWrapper className="d-flex align-items-center me-3 ms-auto">
      <DropdownToggle
        className="nav-link d-flex align-items-center"
        as="a"
        id="orders-basket-import-dropdown"
        data-e2e="orders-basket-import-dropdown-button"
        aria-haspopup="listbox"
      >
        <i className="icon icon-sm me-2">exit_to_app</i>
        <FormattedMessage id="quote.import.button" />
      </DropdownToggle>
      <DropdownMenu role="listbox">
        <SpDropdownItem
          active={false}
          onClick={handleImportFromClipboard}
          data-e2e="orders-basket-import-clipboard"
          disabled={isSumitomo}
        >
          <i className="d-flex icon icon-sm me-2">content_paste_go</i>
          <FormattedMessage id="quote.import.clipboard" />
        </SpDropdownItem>

        <SpDropdownItem
          active={false}
          onClick={handleImportFromFile}
          data-e2e="orders-basket-import-excel"
          disabled={!isSumitomo}
        >
          <i className="d-flex icon icon-sm me-2">upload_file</i>
          <FormattedMessage id="quote.import.excel" />
        </SpDropdownItem>
      </DropdownMenu>
    </DropdownWrapper>
  );
};
